import React from 'react';
import { Link } from 'react-router-dom';
import useIsMobile from '../hooks/useIsMobile';

interface ServiceSection {
  title: string;
  body: string;
  items: string[];
  dark?: boolean;
}

interface ServiceLayoutProps {
  title: string;
  subtitle: string;
  heroImage?: string;
  pageImage?: string;
  pageImageAlt?: string;
  summary: string;
  sections: ServiceSection[];
  ctaText?: string;
  children?: React.ReactNode;
}

const ServiceLayout: React.FC<ServiceLayoutProps> = ({ title, subtitle, heroImage, pageImage, pageImageAlt, summary, sections, ctaText = 'Get Started' }) => {
  const isMobile = useIsMobile();

  return (
    <div className="page" style={{ paddingTop: '80px' }}>
      {/* Hero */}
      <div style={{
        background: heroImage
          ? `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${heroImage}) center/cover no-repeat`
          : 'linear-gradient(135deg, var(--color-navy) 0%, var(--color-primary) 100%)',
        color: 'white',
        padding: isMobile ? '3rem 1.25rem' : '5rem 2rem',
        textAlign: 'center'
      }}>
        <h1 style={{ color: 'white', fontSize: isMobile ? '2rem' : '2.8rem', marginBottom: '1rem' }}>{title}</h1>
        <p style={{ fontSize: isMobile ? '1rem' : '1.2rem', maxWidth: '600px', margin: '0 auto', opacity: 0.9, lineHeight: 1.7 }}>{subtitle}</p>
      </div>

      {/* Breadcrumb */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '1.5rem 2rem 0', textAlign: 'left' }}>
        <Link to="/services" style={{ color: 'var(--color-primary)', textDecoration: 'none', fontWeight: 600, fontSize: '0.95rem' }}>
          ← Services
        </Link>
      </div>

      {/* Intro — two column with image */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '3rem 1.25rem' : '4rem 2rem', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '2rem' : '3rem', alignItems: 'center' }}>
        <div>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '1.1rem', lineHeight: 1.7 }}>{summary}</p>
          <div style={{ marginTop: '2rem' }}>
            <Link to="/contact" style={{ background: 'var(--color-primary)', color: 'white', padding: '0.85rem 2.25rem', borderRadius: '50px', fontWeight: 600, textDecoration: 'none', fontSize: '0.95rem', display: 'inline-block' }}>
              {ctaText} →
            </Link>
          </div>
        </div>
        <div style={{ borderRadius: '14px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', maxHeight: '380px' }}>
          {pageImage ? (
            <picture>
              <source srcSet={pageImage.replace(/\.(jpg|jpeg|png)$/i, '.avif')} type="image/avif" />
              <img src={pageImage} alt={pageImageAlt || title} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
            </picture>
          ) : (
            <div style={{ background: 'var(--color-surface-alt)', aspectRatio: '4/3', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-muted-decorative)' }}>
              <span style={{ fontSize: '2rem' }}>📷</span>
            </div>
          )}
        </div>
      </div>

      {/* Full-width sections */}
      {sections.map((section, i) => {
        const isDark = section.dark ?? (i % 2 === 0);
        return (
          <div key={section.title} style={{
            background: isDark ? 'linear-gradient(135deg, var(--color-navy-deep) 0%, var(--color-navy) 100%)' : 'var(--color-surface)',
            padding: isMobile ? '3rem 1.25rem' : '5rem 2rem',
          }}>
            <div style={{ maxWidth: '860px', margin: '0 auto' }}>
              <h2 style={{
                color: isDark ? 'var(--color-white)' : 'var(--color-text)',
                fontSize: isMobile ? '1.5rem' : '1.75rem',
                fontWeight: 700,
                marginBottom: '1rem',
              }}>{section.title}</h2>
              <p style={{
                color: isDark ? 'var(--color-muted-on-dark-light)' : 'var(--color-text-secondary)',
                fontSize: '1.05rem',
                lineHeight: 1.7,
                marginBottom: '2rem',
              }}>{section.body}</p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '1rem',
              }}>
                {section.items.map((item, j) => (
                  <div key={j} style={{
                    background: isDark ? 'rgba(255,255,255,0.05)' : 'white',
                    border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid var(--color-border)',
                    borderRadius: '10px',
                    padding: '1.25rem',
                  }}>
                    <span style={{
                      color: isDark ? 'var(--color-border)' : 'var(--color-text-label)',
                      fontSize: '0.95rem',
                      lineHeight: 1.7,
                    }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}

      {/* CTA */}
      <div style={{ background: 'linear-gradient(135deg, var(--color-navy) 0%, var(--color-primary) 100%)', color: 'white', padding: '5rem 2rem', textAlign: 'center' }}>
        <h2 style={{ color: 'white', fontSize: isMobile ? '1.75rem' : '2.2rem', marginBottom: '1rem' }}>Ready to Get Started?</h2>
        <p style={{ opacity: 0.9, fontSize: '1.1rem', maxWidth: '500px', margin: '0 auto 2rem' }}>
          Let's talk about your facility's needs and how we can help.
        </p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/contact" style={{ background: 'white', color: 'var(--color-primary)', padding: '1rem 2.5rem', borderRadius: '50px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', display: 'inline-block' }}>
            Get in Touch
          </Link>
          <Link to="/reviews" style={{ background: 'transparent', color: 'white', padding: '1rem 2.5rem', borderRadius: '50px', fontWeight: 600, textDecoration: 'none', fontSize: '1rem', display: 'inline-block', border: '2px solid rgba(255,255,255,0.4)' }}>
            Leave a Review
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ServiceLayout;
